/**
 * Auto-starts the onboarding tour exactly once, the first time an authenticated
 * user reaches the app shell.
 *
 * Gating: <TourProvider> reads the `solandio:tour-done` localStorage flag into
 * `hasSeen`. If the user has already finished/skipped the tour, this renders
 * nothing and never fires. Because this component is mounted *inside* the
 * AppShell (which only renders when the user has an active family — not on the
 * sign-in or no-family onboarding screen), the tour can only auto-start on the
 * real product surface.
 *
 * We defer the start to the next frame so the shell's tour targets (children
 * switcher, module sidebar, user menu) are painted before Joyride measures them.
 */
import { useEffect, useRef } from 'react';
import { useTour } from './TourContext';

export function TourAutoStart() {
  const { hasSeen, run, startTour } = useTour();
  const firedRef = useRef(false);

  useEffect(() => {
    if (hasSeen || run || firedRef.current) return;
    firedRef.current = true;
    const id = window.requestAnimationFrame(() => startTour());
    return () => window.cancelAnimationFrame(id);
  }, [hasSeen, run, startTour]);

  return null;
}
